/* ──────────────────────────────────────────────────────────────
   Setup/Categories/index.tsx · Category tree (live — sk-catalog-api /categories)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  ViewStyle,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import {
  Icon,
  Button,
  Input,
  AppDropdown,
  Modal,
  Empty,
  Popover,
  MenuItem,
  MenuSeparator,
  ConfirmDialog,
  useToast,
} from '../../components/ui';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../../services/catalog/taxonomy';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import type { Category } from '../../schemas/catalog/taxonomy';

// ── Node ─────────────────────────────────────────────────────

interface NodeProps {
  node: Category;
  depth: number;
  openState: Record<string, boolean>;
  setOpen: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onAddSub: (parent: Category) => void;
  onDelete: (node: Category) => void;
  onRename: (node: Category) => void;
}

function Node({ node, depth, openState, setOpen, onAddSub, onDelete, onRename }: NodeProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const hasKids = !!node.children && node.children.length > 0;
  const isOpen = openState[node.id];

  const rowBase: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: radii.r2,
    borderWidth: 1,
    borderColor: 'transparent',
    ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
  };

  return (
    <View>
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => { if (hasKids) setOpen(o => ({ ...o, [node.id]: !o[node.id] })); }}
        style={rowBase}
      >
        <View style={{ width: 16, alignItems: 'center', justifyContent: 'center' }}>
          {hasKids ? (
            <Icon name={isOpen ? 'chev-d' : 'chev-r'} size={14} color={colors.ink3} />
          ) : (
            <Icon name="tag" size={13} color={colors.ink3} />
          )}
        </View>

        <Text style={{ fontWeight: depth === 0 ? fontWeight.semibold : fontWeight.regular, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans, flex: 1 }}>
          {node.name}
        </Text>

        {node.item_count != null && (
          <Text style={{ marginLeft: 'auto', fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.mono }}>
            {node.item_count}
          </Text>
        )}

        <View onStartShouldSetResponder={() => true}>
          <Popover
            align="right"
            width={170}
            trigger={<Button kind="ghost" size="xs" iconOnly icon="more" aria-label="Category actions" />}
          >
            {(close: () => void) => (
              <>
                <MenuItem icon="plus" onPress={() => { close(); onAddSub(node); }}>
                  Add sub-category
                </MenuItem>
                <MenuItem icon="edit" onPress={() => { close(); onRename(node); }}>
                  Rename
                </MenuItem>
                <MenuSeparator />
                <MenuItem icon="trash" danger onPress={() => { close(); onDelete(node); }}>
                  Delete
                </MenuItem>
              </>
            )}
          </Popover>
        </View>
      </TouchableOpacity>

      {hasKids && isOpen && (
        <View style={{ marginLeft: 22, borderLeftWidth: 1, borderLeftColor: colors.line, paddingLeft: 6 }}>
          {node.children!.map(child => (
            <Node
              key={child.id}
              node={child}
              depth={depth + 1}
              openState={openState}
              setOpen={setOpen}
              onAddSub={onAddSub}
              onDelete={onDelete}
              onRename={onRename}
            />
          ))}
        </View>
      )}
    </View>
  );
}

// ── CategoriesView (main export) ──────────────────────────────

export default function CategoriesView() {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [openState, setOpen] = useState<Record<string, boolean>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCategories();
      setCategories(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const onAdd = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await createCategory({
        name: name.trim(),
        ...(parentId ? { parentCategoryId: parentId } : {}),
      });
      showSuccess(res, 'Category added', `“${name.trim()}” created.`);
      setName(''); setParentId(''); setAddOpen(false);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not add category');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = (node: Category) => setDelTarget(node);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteCategory(delTarget.id);
      showSuccess({}, 'Category deleted', `“${delTarget.name}” removed.`);
      await load();
      setDelTarget(null);
    } catch (err) {
      handleApiError(err, 'Could not delete category');
    } finally {
      setDeleting(false);
    }
  };

  const [renameTarget, setRenameTarget] = useState<Category | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const onRename = (node: Category) => { setRenameTarget(node); setRenameValue(node.name); };
  const commitRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    try {
      await updateCategory(renameTarget.id, { name: renameValue.trim() });
      showSuccess({}, 'Category renamed', 'Updated.');
      setRenameTarget(null);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not rename category');
    }
  };

  const sectionCardStyle: ViewStyle = {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    ...(shadows.sm as ViewStyle),
  };

  // Flat top-level options for the parent selector.
  const parentOptions = [
    { label: '— Top level —', value: '' },
    ...categories.map(c => ({ label: c.name, value: c.id })),
  ];

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>
            Categories
          </Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>
            A nested tree to organise items.
          </Text>
        </View>
        <Button kind="accent" icon="plus" onPress={() => { setParentId(''); setAddOpen(true); }}>
          Add category
        </Button>
      </View>

      <View style={[sectionCardStyle, { maxWidth: 760 }]}>
        <View style={{ padding: 16 }}>
          {loading ? (
            <View style={{ paddingVertical: 32, alignItems: 'center' }}>
              <ActivityIndicator color={colors.accent} />
            </View>
          ) : categories.length === 0 ? (
            <Empty title="No categories yet">Create your first category to organise items.</Empty>
          ) : (
            categories.map(c => (
              <Node
                key={c.id}
                node={c}
                depth={0}
                openState={openState}
                setOpen={setOpen}
                onAddSub={(parent) => { setParentId(parent.id); setAddOpen(true); }}
                onDelete={onDelete}
                onRename={onRename}
              />
            ))
          )}
        </View>
      </View>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        size="sm"
        title="Add category"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddOpen(false)}>Cancel</Button>
            <Button kind="accent" disabled={!name.trim() || busy} onPress={onAdd}>
              {busy ? 'Adding…' : 'Add'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          <Input label="Category name" placeholder="e.g. Cold brews" value={name} onChangeText={setName} autoFocus />
          <AppDropdown placeholder="Parent" value={parentId} items={parentOptions} onSelect={setParentId} isDark={isDark} />
        </View>
      </Modal>

      <Modal
        open={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        size="sm"
        title="Rename category"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setRenameTarget(null)}>Cancel</Button>
            <Button kind="accent" disabled={!renameValue.trim()} onPress={commitRename}>Save</Button>
          </View>
        }
      >
        <Input label="Category name" value={renameValue} onChangeText={setRenameValue} autoFocus />
      </Modal>

      <ConfirmDialog
        visible={!!delTarget}
        title="Delete category?"
        message={delTarget ? `"${delTarget.name}" will be deleted. Items lose this category.` : ''}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDelTarget(null)}
        onClose={() => setDelTarget(null)}
      />
    </ScrollView>
  );
}
